type ApiResponse<T> = {
    data?: T;
    error?: string;
};

type ErrorMessage = {
    title: string;
    message: string | Record<string, string[]>;
};

type ErrorResponse = {
    status_code: number;
    message: ErrorMessage;
};

// Helper function to format error message
function formatErrorMessage(message: ErrorMessage): string {
    if (typeof message.message === "string") {
        return message.message;
    }

    // Handle case where message contains field errors
    return Object.entries(message.message)
        .map(([, errors]) => errors.join(", "))
        .join("\n");
}

// POST request

export async function apiPost<T, D = Record<string, unknown> | FormData>(
    endpoint: string,
    data: D,
    options?: RequestInit
): Promise<ApiResponse<T>> {
    const isFormData = data instanceof FormData;
    try {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                ...options?.headers,
            },
            ...options,
            body: isFormData ? data : JSON.stringify(data),
        });

        const responseData = await response.json();


        // Handle successful response (200)
        if (response.ok) {
            return {
                data: responseData,
            };
        }

        // Handle error responses
        const errorResponse = responseData as ErrorResponse;
        return {
            error: errorResponse.message
                ? formatErrorMessage(errorResponse.message)
                : "Something went wrong",
        };
    } catch (error) {
        return {
            error:
                error instanceof Error ? error.message : "Something went wrong",
        };
    }
}

// GET request

export async function apiGet<T>(
    endpoint: string,
    options?: RequestInit
): Promise<ApiResponse<T>> {
    try {
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                // "Content-Type": "application/json; charset=UTF-8",
                ...options?.headers,
            },
            ...options,
        });

        const responseData = await response.json();

        if (!response.ok) {
            const errorResponse = responseData as ErrorResponse;
            return {
                error: errorResponse.message
                    ? formatErrorMessage(errorResponse.message)
                    : "Something went wrong",
            };
        }

        return { data: responseData };
    } catch (error) {
        return {
            error:
                error instanceof Error ? error.message : "Something went wrong",
        };
    }
}

// DELETE request

export async function apiDelete<T>(
    endpoint: string,
    options?: RequestInit
): Promise<ApiResponse<T>> {
    try {
        const response = await fetch(endpoint, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
                ...options?.headers,
            },
            ...options,
        });

        const responseData = await response.json();

        if (!response.ok) {
            const errorResponse = responseData as ErrorResponse;
            return {
                error: errorResponse.message
                    ? formatErrorMessage(errorResponse.message)
                    : "Something went wrong",
            };
        }

        return { data: responseData };
    } catch (error) {
        return {
            error:
                error instanceof Error ? error.message : "Something went wrong",
        };
    }
}

// PUT request

export async function apiPut<T, D = Record<string, unknown> | FormData>(
    endpoint: string,
    data: D,
    options?: RequestInit
): Promise<ApiResponse<T>> {
    const isFormData = data instanceof FormData;
    try {
        const response = await fetch(endpoint, {
            method: "PUT",
            headers: {
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                ...options?.headers,
            },
            ...options,
            body: isFormData ? data : JSON.stringify(data),
        });

        const responseData = await response.json();

        if (!response.ok) {
            const errorResponse = responseData as ErrorResponse;
            return {
                error: errorResponse.message
                    ? formatErrorMessage(errorResponse.message)
                    : "Something went wrong",
            };
        }

        return { data: responseData };
    } catch (error) {
        return {
            error:
                error instanceof Error ? error.message : "Something went wrong",
        };
    }
}
